import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  HairlineCard,
  Icons,
  iconSize,
  MenuItem,
  Stack,
  StatusChip,
  TextField,
  Typography,
  fontStacks,
  palette,
  useSnackbar,
} from '@munifocus/ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FILE_PRIORITIES,
  PRIORITY_REASONS,
  normalizeTag,
  officerIneligibility,
  productivityEnabledFor,
  requiresPriorityReason,
  type BulkFileAction,
  type BulkFileActionDto,
  type BulkFileActionResponse,
  type FilePriority,
  type FileSummary,
  type PriorityReason,
} from '@munifocus/contracts-gov';
import {
  downloadBlob,
  exportFilesCsv,
  useBulkFileAction,
  useProductivitySettings,
} from '../../lib/productivity-api';
import { useLocalGovernmentOfficers } from '../../lib/local-governments-api';
import { apiErrorMessage } from '../../lib/mutation-toast';

type Dialogs = BulkFileAction | 'result' | null;

/**
 * Act on the selected files at once: reassign, set priority, add or remove a
 * tag, or export them as CSV.
 *
 * The server checks every file on its own and answers file by file, so a batch
 * where two files are out of reach still does the other thirty-eight — and the
 * result dialog says which two, and why. Each changed file gets exactly one
 * entry in its own audit trail, stamped with the batch id.
 */
export function BulkActionBar({
  selected,
  onClear,
}: {
  selected: FileSummary[];
  onClear: () => void;
}) {
  const { t, i18n } = useTranslation();
  const snackbar = useSnackbar();
  const settings = useProductivitySettings();
  const bulk = useBulkFileAction();
  const [dialog, setDialog] = useState<Dialogs>(null);
  const [result, setResult] = useState<BulkFileActionResponse | null>(null);
  const [exporting, setExporting] = useState(false);

  const [officerId, setOfficerId] = useState('');
  const [priority, setPriority] = useState<FilePriority>('routine');
  const [reason, setReason] = useState<PriorityReason | ''>('');
  const [reasonOther, setReasonOther] = useState('');
  const [tag, setTag] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Mutating actions are offered when at least one selected file is at a
  // municipality with the tools on; the server refuses the rest file by file.
  const canMutate = selected.some((f) =>
    productivityEnabledFor(settings.data, f.localGovernmentId),
  );

  // A roster is per municipality, so reassigning needs one.
  const lgIds = useMemo(
    () => [...new Set(selected.map((f) => f.localGovernmentId).filter(Boolean))] as string[],
    [selected],
  );
  const singleLg = lgIds.length === 1 ? lgIds[0] : null;
  const roster = useLocalGovernmentOfficers(dialog === 'reassign' ? singleLg : null);

  const references = useMemo(() => new Map(selected.map((f) => [f.id, f.reference])), [selected]);

  const open = (action: BulkFileAction) => {
    setSubmitted(false);
    setOfficerId('');
    setPriority('routine');
    setReason('');
    setReasonOther('');
    setTag('');
    setDialog(action);
  };

  const tagValue = normalizeTag(tag);
  const needsReason = requiresPriorityReason(priority);

  const run = (action: BulkFileAction) => {
    setSubmitted(true);
    const dto: BulkFileActionDto = { fileIds: selected.map((f) => f.id), action };
    if (action === 'reassign') dto.officerId = officerId || null;
    if (action === 'set_priority') {
      if (needsReason && !reason) return;
      if (reason === 'other' && !reasonOther.trim()) return;
      dto.priority = priority;
      if (needsReason) {
        dto.priorityReason = reason as PriorityReason;
        if (reason === 'other') dto.priorityReasonOther = reasonOther.trim();
      }
    }
    if (action === 'add_tag' || action === 'remove_tag') {
      if (!tagValue) return;
      dto.tag = tagValue;
    }
    bulk.mutate(dto, {
      onSuccess: (res) => {
        // Everything done: a toast is enough and the selection is spent. Any
        // refusal opens the per-file report instead, so it is not missed.
        if (res.failed === 0) {
          setDialog(null);
          snackbar.success(t('gov.bulk.doneAll', { count: res.succeeded }));
          onClear();
          return;
        }
        setResult(res);
        setDialog('result');
      },
      onError: (err) => snackbar.error(apiErrorMessage(err, t('gov.bulk.failed'))),
    });
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
      const blob = await exportFilesCsv({ fileIds: selected.map((f) => f.id) }, lang);
      const stamp = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `${t('gov.bulk.exportFilename')}-${stamp}.csv`);
      snackbar.success(t('gov.bulk.exported', { count: selected.length }));
    } catch (err) {
      snackbar.error(apiErrorMessage(err, t('gov.bulk.exportFailed')));
    } finally {
      setExporting(false);
    }
  };

  if (selected.length === 0) return null;

  const actionTitle = (action: BulkFileAction) =>
    t(`gov.bulk.actions.${action}`) + ' · ' + t('gov.bulk.selected', { count: selected.length });

  return (
    <>
      <HairlineCard
        role="region"
        aria-label={t('gov.bulk.region')}
        sx={{
          p: 1.5,
          mb: 1.5,
          position: 'sticky',
          top: { xs: 8, md: 72 },
          zIndex: 5,
          backgroundColor: palette.brand.accentTint,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between' }}
        >
          <Typography sx={{ fontWeight: 600 }} aria-live="polite">
            {t('gov.bulk.selected', { count: selected.length })}
          </Typography>
          <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {canMutate && (
              <>
                <Button
                  size="small"
                  startIcon={<Icons.UserPlus size={iconSize.sm} />}
                  onClick={() => open('reassign')}
                >
                  {t('gov.bulk.actions.reassign')}
                </Button>
                <Button
                  size="small"
                  startIcon={<Icons.AlertTriangle size={iconSize.sm} />}
                  onClick={() => open('set_priority')}
                >
                  {t('gov.bulk.actions.set_priority')}
                </Button>
                <Button
                  size="small"
                  startIcon={<Icons.Tag size={iconSize.sm} />}
                  onClick={() => open('add_tag')}
                >
                  {t('gov.bulk.actions.add_tag')}
                </Button>
                <Button size="small" onClick={() => open('remove_tag')}>
                  {t('gov.bulk.actions.remove_tag')}
                </Button>
              </>
            )}
            <Button
              size="small"
              startIcon={<Icons.Download size={iconSize.sm} />}
              onClick={() => void onExport()}
              disabled={exporting}
            >
              {t('gov.bulk.export')}
            </Button>
            <Button size="small" onClick={onClear}>
              {t('gov.bulk.clear')}
            </Button>
          </Stack>
        </Stack>
      </HairlineCard>

      <Dialog
        open={dialog !== null && dialog !== 'result'}
        onClose={() => setDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        {dialog && dialog !== 'result' && (
          <>
            <DialogTitle>{actionTitle(dialog)}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                {dialog === 'reassign' &&
                  (singleLg ? (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label={t('gov.file.assignedOfficer')}
                      value={officerId}
                      onChange={(e) => setOfficerId(e.target.value)}
                      disabled={roster.isLoading}
                    >
                      <MenuItem value="">{t('gov.file.assignedOfficerNone')}</MenuItem>
                      {(roster.data ?? []).map((o) => {
                        const why = officerIneligibility(o);
                        return (
                          <MenuItem key={o.userId} value={o.userId} disabled={!!why}>
                            {o.displayName}
                            {o.badgeNumber ? ` · ${o.badgeNumber}` : ''}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  ) : (
                    <Alert severity="info">{t('gov.bulk.reassignOnePlace')}</Alert>
                  ))}

                {dialog === 'set_priority' && (
                  <>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label={t('gov.file.priority')}
                      value={priority}
                      onChange={(e) => setPriority(e.target.value as FilePriority)}
                    >
                      {FILE_PRIORITIES.map((p) => (
                        <MenuItem key={p} value={p}>
                          {t(`gov.priority.${p}`)}
                        </MenuItem>
                      ))}
                    </TextField>
                    {needsReason && (
                      <TextField
                        select
                        fullWidth
                        size="small"
                        required
                        label={t('gov.bulk.priorityReason')}
                        value={reason}
                        error={submitted && !reason}
                        helperText={
                          submitted && !reason ? t('common.validation.choose') : undefined
                        }
                        onChange={(e) => setReason(e.target.value as PriorityReason)}
                      >
                        {PRIORITY_REASONS.map((r) => (
                          <MenuItem key={r} value={r}>
                            {t(`gov.priorityReason.${r}`)}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                    {needsReason && reason === 'other' && (
                      <TextField
                        fullWidth
                        size="small"
                        required
                        label={t('gov.bulk.priorityReasonOther')}
                        value={reasonOther}
                        error={submitted && !reasonOther.trim()}
                        onChange={(e) => setReasonOther(e.target.value)}
                      />
                    )}
                  </>
                )}

                {(dialog === 'add_tag' || dialog === 'remove_tag') && (
                  <TextField
                    fullWidth
                    size="small"
                    autoFocus
                    label={t('gov.bulk.tag')}
                    value={tag}
                    error={submitted && !tagValue}
                    helperText={
                      submitted && !tagValue ? t('gov.bulk.tagRequired') : t('gov.bulk.tagHint')
                    }
                    onChange={(e) => setTag(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        run(dialog);
                      }
                    }}
                  />
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialog(null)} disabled={bulk.isPending}>
                {t('common.actions.cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={() => run(dialog)}
                disabled={bulk.isPending || (dialog === 'reassign' && !singleLg)}
              >
                {bulk.isPending
                  ? t('common.actions.saving')
                  : t('gov.bulk.apply', { count: selected.length })}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={dialog === 'result'} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('gov.bulk.resultTitle')}</DialogTitle>
        <DialogContent>
          {result && (
            <Stack spacing={1.5}>
              <Typography>
                {t('gov.bulk.resultSummary', {
                  succeeded: result.succeeded,
                  failed: result.failed,
                })}
              </Typography>
              {result.failed > 0 && (
                <Box component="ul" sx={{ m: 0, p: 0 }}>
                  {result.results
                    .filter((r) => !r.ok)
                    .map((r) => (
                      <Stack
                        key={r.fileId}
                        component="li"
                        direction="row"
                        spacing={1}
                        sx={{
                          listStyle: 'none',
                          py: 0.75,
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderTop: `1px solid ${palette.border.line}`,
                        }}
                      >
                        <Typography sx={{ fontFamily: fontStacks.mono, fontSize: 13 }}>
                          {r.reference ?? references.get(r.fileId) ?? r.fileId}
                        </Typography>
                        <StatusChip
                          status="warning"
                          label={t(`gov.bulk.errors.${r.error ?? 'failed'}`)}
                        />
                      </Stack>
                    ))}
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialog(null);
              if (result && result.failed > 0) onClear();
            }}
          >
            {t('common.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
